import { useCallback } from 'react'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { NoticeKey } from '../../../../models/Notice'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'
import { useGetNoticesQuery, useUpdateNoticeMutation } from '../../../../store/notices/notices'
import { useCalendarCapability } from '../../../../store/onboarding/useCalendarCapability'
import { useMailCapability } from '../../../../store/onboarding/useMailCapability'
import { useZoomCapability } from '../../../../store/onboarding/useZoomCapability'
import { useZocksQuota } from '../../../../components/ScopedBlock/hooks'

export interface UseUpcomingOnboardingBannerResult {
  shouldShow: boolean
  isFetching: boolean
  dismiss: () => void
  isDismissing: boolean
}

export interface UseCalendarOnboardingBannerResult extends UseUpcomingOnboardingBannerResult {
  allowedCalendarIds: IntegrationServiceId[]
  allowedCalendarSecondaryIds: IntegrationServiceId[]
  connectedWithoutSync: IntegrationServiceId[]
}

export interface UseEmailOnboardingBannerResult extends UseUpcomingOnboardingBannerResult {
  allowedMailIds: IntegrationServiceId[]
}

export const useCalendarOnboardingBanner = (): UseCalendarOnboardingBannerResult => {
  const accountId = useAppSelector(selectAccountId)
  const calendarCapability = useCalendarCapability()

  const { data: noticesData, isFetching: isNoticesFetching } = useGetNoticesQuery({ accountId }, { skip: calendarCapability.isLoading })
  const [updateNotice, { isLoading: isDismissing }] = useUpdateNoticeMutation()

  const isDismissed = noticesData?.data?.find((n) => n.key === NoticeKey.UPCOMING_EMPTY_STATE)?.isDismissed ?? false

  const dismiss = useCallback(() => {
    updateNotice({ accountId, key: NoticeKey.UPCOMING_EMPTY_STATE, isDismissed: true })
  }, [accountId, updateNotice])

  return {
    shouldShow: calendarCapability.isAllowed && !calendarCapability.isFulfilled && !isDismissed,
    isFetching: calendarCapability.isLoading || isNoticesFetching || isDismissing,
    dismiss,
    isDismissing,
    allowedCalendarIds: calendarCapability.allowedServiceIds,
    allowedCalendarSecondaryIds: calendarCapability.allowedSecondaryIds ?? [],
    connectedWithoutSync: calendarCapability.connectedWithoutSync,
  }
}

export const useZoomOnboardingBanner = (): UseUpcomingOnboardingBannerResult => {
  const accountId = useAppSelector(selectAccountId)
  const zoomCapability = useZoomCapability()

  const { data: noticesData, isFetching: isNoticesFetching } = useGetNoticesQuery({ accountId }, { skip: zoomCapability.isLoading })
  const [updateNotice, { isLoading: isDismissing }] = useUpdateNoticeMutation()

  const isDismissed = noticesData?.data?.find((n) => n.key === NoticeKey.UPCOMING_ZOOM_ONBOARDING_BANNER)?.isDismissed ?? false

  const dismiss = useCallback(() => {
    updateNotice({ accountId, key: NoticeKey.UPCOMING_ZOOM_ONBOARDING_BANNER, isDismissed: true })
  }, [accountId, updateNotice])

  return {
    shouldShow: zoomCapability.isAllowed && !zoomCapability.isFulfilled && !isDismissed,
    isFetching: zoomCapability.isLoading || isNoticesFetching || isDismissing,
    dismiss,
    isDismissing,
  }
}

export const useEmailOnboardingBanner = (): UseEmailOnboardingBannerResult => {
  const accountId = useAppSelector(selectAccountId)
  const mailCapability = useMailCapability()
  const { hasQuota, isFetching: isQuotaFetching } = useZocksQuota()

  const { data: noticesData, isFetching: isNoticesFetching } = useGetNoticesQuery({ accountId }, { skip: mailCapability.isLoading })
  const [updateNotice, { isLoading: isDismissing }] = useUpdateNoticeMutation()

  const isDismissed = noticesData?.data?.find((n) => n.key === NoticeKey.UPCOMING_EMAIL_ONBOARDING_BANNER)?.isDismissed ?? false

  const emailEnabled = hasQuota('integration.emailEnabled.value')

  const dismiss = useCallback(() => {
    updateNotice({ accountId, key: NoticeKey.UPCOMING_EMAIL_ONBOARDING_BANNER, isDismissed: true })
  }, [accountId, updateNotice])

  return {
    shouldShow: mailCapability.isAllowed && !mailCapability.isFulfilled && !isDismissed && !emailEnabled,
    isFetching: mailCapability.isLoading || isNoticesFetching || isQuotaFetching || isDismissing,
    dismiss,
    isDismissing,
    allowedMailIds: mailCapability.allowedServiceIds,
  }
}
